import type { VoidFunctionComponent } from 'react';

import { BINARY_DECOMPOSITION } from '../../../../../../shared/featureFlags/flagNames';
import { fetcherToPlainText } from '../../../../../../shared/fetchers';
import Locator from '../../../../../../shared/Locator';
import { useIsFeatureEnabled } from '../../../../../helpers/features';
import { CopiableText } from '../../../../generic';
import Button, { BUTTON_TYPE } from '../../../../generic/Button';
import FetcherIcon from '../../../../generic/FetcherIcon';
import ListRowDetailsSeparator from '../../../../generic/List/ListRowDetailsSeparator';
import Tooltip from '../../../../generic/Tooltip';
import PartialResultGate from '../../../generic/PartialResult/PartialResultGate';
import type { Dependency } from '../../../../../../shared/dependenciesV2';
import type { Fetcher } from '../../../../../../shared/fetchers';
import type { DependencyV2Modals } from '../../hooks/useDependencyModals';

import styles from './DependencyRow.module.less';

type Props = {
  dependency: Dependency;
  modals: DependencyV2Modals;
  isProjectScope: boolean;
};

const DependencyData: VoidFunctionComponent<Props> = ({ dependency, modals, isProjectScope }) => {
  const revision = new Locator(dependency.locator);
  const fetcher = revision.fetcher.full || '';
  const fetcherName = fetcherToPlainText(fetcher as Fetcher) ?? 'Unknown';
  const locator = revision.toFullString();
  const originPathsText = dependency.originPaths.join(', ');
  const isTransitive = dependency.depth > 1;
  const isBinary = fetcher === 'csbinary';
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  // Release groups might have multiple CPEs for the same dependency, but they are most likely the same.
  // For now, we just show the first one.
  const cpe = dependency.cpes[0];

  const openPathModal = () =>
    modals.viewRelationshipsModal.onOpen({
      context: { title: dependency.title, revision: locator },
    });

  const openBinaryMatchDetailsModal = () =>
    modals.viewBinaryMatchDetailsModal.onOpen({
      context: { revision: locator },
    });

  return (
    <div className={styles.DependencyInfoData}>
      <div className={styles.fetcher}>
        <Tooltip id={`${dependency.locator}-fetcher`} tooltip={fetcherName}>
          {/* Note that this span is required to enable the tooltip on an SVG. */}
          <span>
            <FetcherIcon fetcher={fetcher} />
          </span>
        </Tooltip>
      </div>
      <ListRowDetailsSeparator />
      <CopiableText id={locator} name="locator" value={locator} />
      {dependency.originPaths && dependency.originPaths.length > 0 && (
        <>
          <ListRowDetailsSeparator />
          <Tooltip id={`${locator}-originpaths`} tooltip={`Origin: ${originPathsText}`}>
            <span className={styles.originPaths}>{originPathsText}</span>
          </Tooltip>
        </>
      )}
      {cpe && (
        <>
          <ListRowDetailsSeparator />
          <CopiableText id={locator} name="CPE" value={cpe} />
        </>
      )}
      {isTransitive && isProjectScope && (
        <>
          <ListRowDetailsSeparator />
          <Button type={BUTTON_TYPE.ANCHOR} onClick={openPathModal}>
            View path
          </Button>
        </>
      )}
      {isBinary && isBinaryDecompositionEnabled && (
        <PartialResultGate>
          <ListRowDetailsSeparator />
          <Button type={BUTTON_TYPE.ANCHOR} onClick={openBinaryMatchDetailsModal}>
            Match Details
          </Button>
        </PartialResultGate>
      )}
    </div>
  );
};

export default DependencyData;
